<script lang='ts'>
export default {
  name: 'KkWdayIndexRegularHourCard',
}
</script>
<script setup lang='ts'>
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

import ImgIconPlusRed from '../../../../../../../../images/common/icon_plus_red.svg'
import iconCrossPath from '../../../../../../../../images/common/icon_cross.svg'
import ValidationError from '../../../../../../components/ValidationError.vue'
import { KkSelect, KkCheckbox } from '../../../../../../components/base'

const { t } = useI18n()

interface FoodOption {
  lastOrderTime?: string
  kind?: string
}
interface Period {
  openTime: string
  closeTime: string
  foodOption?: FoodOption
}

interface BusinessHour {
  isNotSet: boolean
  noChange? : boolean
  periods: Period[]
  indexes: number[]
  wdays: string[]
}

type Props = {
  businessHour: BusinessHour
  index: string,
  moreHour?: boolean,
  canRemove: boolean,
  atBulk?: boolean,
  availableOption?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  businessHour: () => ({
    isNotSet: true,
    periods: [],
    indexes: [],
    wdays: [],
    moreHour: false,
    canRemove: false,
    atBulk: false,
    availableOption: false
  }),
})

interface Emits {
  (e: 'removeSetting'): void
  (e: 'set-field-value', target: string, value: string): void
}
const emit = defineEmits<Emits>()

const isOpen = computed<boolean>(() => props.businessHour.periods.length !== 0)
const showNotSet = computed<boolean>(() => !props.atBulk)

interface WdayIndex {
  label: string;
  value: number;
}
interface Wday {
  label: string;
  value: string;
}
const wdayIndexes: WdayIndex[] = [{label: '第1週目', value : 1}, {label: '第2週目', value : 2}, {label: '第3週目', value : 3}, {label: '第4週目', value : 4}, {label: '第5週目', value : 5}]
const wdays: Wday[] = [{label: '月', value : 'monday'},{label: '火', value : 'tuesday'}, {label: '水', value : 'wednesday'}, {label: '木', value : 'thursday'},{label: '金', value : 'friday'}, {label: '土', value : 'saturday'},{label: '日', value : 'sunday'}]

const is24h = (businessHour: BusinessHour): boolean => {
  if (businessHour.periods.length >= 2) return false // 24時間営業の時はperiods.lengthは1になるため、それ以上あればfalse

  const period = businessHour.periods[0]
  return period?.openTime === '00:00' && period?.closeTime === '24:00'
}
const set24h = (val: BusinessHour): void => {
  val.noChange = false
  val.isNotSet = false
  val.periods = [{openTime: '00:00', closeTime: '24:00'}]
}
const createPeriod = (val: BusinessHour): void => {
  resetFormValues(0)
  val.noChange = false
  val.isNotSet = false
  if (props.availableOption) {
    val.periods = [{
      openTime: '',
      closeTime: '',
      foodOption: {
        lastOrderTime: '',
        kind: ''
      }
    }]
  } else {
    val.periods = [{
      openTime: '',
      closeTime: '',
    }]
  }
}
const removePeriods = (val: BusinessHour): void => {
  val.noChange = false
  val.isNotSet = false
  val.periods = []
}
const setNothing = (val: BusinessHour): void => {
  val.noChange = true
  val.isNotSet = true
  val.indexes = []
  val.wdays = []
  val.periods = []
}
const addPeriod = (val: BusinessHour): void => {
  resetFormValues(val.periods.length)
  if (props.availableOption) {
    val.periods.push({openTime: '', closeTime: '', foodOption: { lastOrderTime: '', kind: '' }})
  } else {
    val.periods.push({openTime: '', closeTime: ''})
  }
}
const removePeriod = (val: BusinessHour, key: number): void => {
  val.periods.splice(key, 1)
}
const removeSetting = (index: string): void => {
  emit('removeSetting')
}
const resetFormValues = (num: number) => {
  // ValidationProviderのvalueの値が残ってしまうことがあるので、時間を追加するたびにresetするようにする
  emit('set-field-value', `regularHourStartForm_${props.index}_${num}`, '')
  emit('set-field-value', `regularHourEndForm_${props.index}_${num}`, '')
  emit('set-field-value', `regularHourLastOrderForm_${props.index}_${num}`, '')
  emit('set-field-value', `regularHourTimeLabelForm_${props.index}_${num}`, '')
}

interface TimeLabelOptions {
  [key: string]: string;
}
const timeLabelOptions = (): TimeLabelOptions => {
  let timeLabelOptions: TimeLabelOptions = {}
  timeLabelOptions[t('labels.notSet')] = ''
  const timeLabelList = ['morning', 'lunch', 'dinner']
  for (let timeLabel of timeLabelList) {
    timeLabelOptions[t(`pages.CompaniesLocationStore.businessHours.regularHours.timeLabel.${timeLabel}`)] = timeLabel
  }
  return timeLabelOptions
}

const handleChangeWdayIndex = (wIndex: number) => {
  if (props.businessHour.indexes.includes(wIndex)) {
    setWdayIndexes(props.businessHour, props.businessHour.indexes.filter(v => v !== wIndex))
  } else {
    addWdayIndex(props.businessHour, wIndex)
  }
}
const setWdayIndexes = (businessHour: BusinessHour, val: number[]) => {
  businessHour.indexes = val
}
const addWdayIndex = (businessHour: BusinessHour, val: number) => {
  businessHour.indexes.push(val)
}
const setWday = (wday: string) => {
  if (props.businessHour.wdays.includes(wday)) {
    setWdays(props.businessHour, props.businessHour.wdays.filter(v => v !== wday))
  } else {
    addWday(props.businessHour, wday)
  }
}
const setWdays = (businessHour: BusinessHour, val: string[]) => {
  businessHour.wdays = val
}
const addWday = (businessHour: BusinessHour, val: string) => {
  businessHour.wdays.push(val)
}
</script>
<template>
  <div class="p-box__body">
    <div class="p-RegularHour__header">
      <div class="p-RegularHour__label">
        <span>{{ t('pages.CompaniesLocationStore.businessHours.wdayIndexRegularHours.cardTitle') }}</span>
      </div>
      <span v-if="showNotSet" class="c-radio__item">
        <input
          :id="`wdayIndex_not_set_checkbox_${index}`"
          :checked="businessHour.isNotSet"
          type="radio"
          class="c-radio__input"
          @change="setNothing(businessHour)"
        >
        <label
          class="c-radio__label"
          :for="`wdayIndex_not_set_checkbox_${index}`"
        >{{ moreHour ? $t('models.moreHour.notSet') : $t('models.storeRegularHour.notSet') }}</label>
      </span>
      <span class="c-radio__item">
        <input
          :id="`wdayIndex_close_checkbox_${index}`"
          :checked="!businessHour.noChange && !businessHour.isNotSet && !isOpen"
          type="radio"
          class="c-radio__input"
          @change="removePeriods(businessHour)"
        >
        <label
          class="c-radio__label"
          :for="`wdayIndex_close_checkbox_${index}`"
        >{{ moreHour ? $t('models.moreHour.notSet') : $t('models.storeRegularHour.regularHoliday') }}</label>
      </span>
      <span class="c-radio__item">
        <input
          :id="`wdayIndex_open_checkbox_${index}`"
          :checked="!businessHour.noChange && !businessHour.isNotSet && isOpen && !is24h(businessHour)"
          type="radio"
          class="c-radio__input"
          @change="createPeriod(businessHour)"
        >
        <label
          class="c-radio__label"
          :for="`wdayIndex_open_checkbox_${index}`"
        >{{ $t('models.storeRegularHour.businessDay') }}</label>
      </span>
      <span class="c-radio__item">
        <input
          :id="`wdayIndex_24h_open_checkbox_${index}`"
          :checked="!businessHour.noChange && !businessHour.isNotSet && isOpen && is24h(businessHour)"
          type="radio"
          class="c-radio__input"
          @change="set24h(businessHour)"
        >
        <label
          class="c-radio__label"
          :for="`wdayIndex_24h_open_checkbox_${index}`"
        >{{ $t('models.storeRegularHour.open24hours') }}</label>
      </span>
    </div>

    <template v-if="!businessHour.noChange && !businessHour.isNotSet">
      <ValidationProvider
        v-slot="{ handleChange, errors }"
        :rules="{
          check_min: [1, businessHour.indexes],
        }"
        :vid="`index_error_${index}`"
        :name="$t('labels.date.week')"
      >
        <div class="p-RegularHour__combination">
          <KkCheckbox
            v-for="(wdayIndex, i) in wdayIndexes"
            :id="`${wdayIndex.value}_ordinal_checkbox_${index}`"
            :key="i"
            class="p-RegularHour__combination__checkbox"
            :model-value="businessHour.indexes.includes(wdayIndex.value)"
            :label="wdayIndex.label"
            @change="handleChangeWdayIndex(wdayIndex.value); handleChange();"
          />
        </div>
        <ValidationError :error="errors[0]" />
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ handleChange, errors }"
        :rules="{
          check_min: [1, businessHour.wdays],
        }"
        :vid="`wday_error_${index}`"
        :name="$t('labels.date.wday')"
      >
        <div class="p-RegularHour__combination">
          <KkCheckbox
            v-for="(wday, i) in wdays"
            :id="`${wday.value}_ordinal_checkbox_${index}`"
            :key="i"
            class="p-RegularHour__combination__checkbox"
            :model-value="businessHour.wdays.includes(wday.value)"
            :label="wday.label"
            @change="setWday(wday.value); handleChange();"
          />
        </div>
        <ValidationError :error="errors[0]" />
      </ValidationProvider>
    </template>
    <template v-if="isOpen && !is24h(businessHour)">
      <div v-for="(period, k) in businessHour.periods" :key="`wdayIndex_${index}_${k}`" class="p-RegularHour__form">
        <fieldset class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__2 p-formGrid__item__l__2">
          <label class="c-formLabel">{{ $t('labels.time.start') }}</label>
          <ValidationProvider
            v-slot="{ errors, value, handleChange }"
            :key="`start_${index}_${k}`"
            v-model="period.openTime"
            :vid="`regularHourStartForm_${index}_${k}`"
            :rules="{
              required: true,
              time_format: true,
              after_pre_time: `@regularHourEndForm_${index}_${k - 1}`,
            }"
            :name="$t('labels.time.start')"
          >
            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.time.start')" @input="handleChange">
            <ValidationError :error="errors[0]" />
          </ValidationProvider>
        </fieldset>
        <fieldset class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__2 p-formGrid__item__l__2">
          <label class="c-formLabel">{{ $t('labels.time.end') }}</label>
          <ValidationProvider
            v-slot="{ errors, value, handleChange }"
            :key="`end_${index}_${k}`"
            v-model="period.closeTime"
            :vid="`regularHourEndForm_${index}_${k}`"
            :rules="{
              required: true,
              time_format: true,
            }"
            :name="$t('labels.time.end')"
          >
            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.time.end')" @input="handleChange">
            <ValidationError :error="errors[0]" />
          </ValidationProvider>
        </fieldset>
        <fieldset v-if="props.availableOption && period.foodOption" class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__2 p-formGrid__item__l__2">
          <label class="c-formLabel">{{ $t('labels.time.lastOrder') }}{{ $t('labels.optionalLabel') }}</label>
          <ValidationProvider
            v-slot="{ errors, value, handleChange }"
            :key="`lastOrderTime_${index}_${k}`"
            v-model="period.foodOption.lastOrderTime"
            :vid="`regularHourLastOrderForm_${index}_${k}`"
            :rules="{
              time_format: true,
              valid_last_order: [`@regularHourStartForm_${index}_${k}`, `@regularHourEndForm_${index}_${k}`]
            }"
            :name="$t('labels.time.lastOrder')"
          >
            <input :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.time.lastOrder')" @input="handleChange">
            <ValidationError :error="errors[0]" />
          </ValidationProvider>
        </fieldset>
        <fieldset v-if="props.availableOption && period.foodOption" class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__3 p-formGrid__item__l__3">
          <label class="c-formLabel">{{ $t('labels.time.kind') }}{{ $t('labels.optionalLabel') }}</label>
          <ValidationProvider
            v-slot="{ errors, value, handleChange }"
            :key="`kind_${index}_${k}`"
            v-model="period.foodOption.kind"
            :vid="`regularHourTimeLabelForm_${index}_${k}`"
            rules=""
            :name="$t('labels.time.kind')"
          >
            <KkSelect
              :options="timeLabelOptions()"
              :model-value="value"
              @update:model-value="handleChange"
            />
            <ValidationError :error="errors[0]" />
          </ValidationProvider>
        </fieldset>

        <fieldset class="p-RegularHour-period__form p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__3 p-formGrid__item__l__3">
          <div v-if="businessHour.periods.length > 1" class="p-RegularHour-period-remove">
            <span class="p-RegularHour-period-remove__button" @click="removePeriod(businessHour, k)">
              <img :src="iconCrossPath" alt="" class="p-stores__action__icon">
              {{ $t('labels.delete') }}
            </span>
          </div>
        </fieldset>
      </div>
      <div class="p-RegularHour-period__add c-button c-button--icon">
        <div class="c-button__item c-button__item--primary" @click="addPeriod(businessHour)">
          <img :src="ImgIconPlusRed" alt="" class="c-button__icon">
          <span>{{ $t('labels.add', { value: $t('models.storeRegularHour.modelName') }) }}（{{ $t('models.storeRegularHour.caseOfMultiHours') }}）</span>
        </div>
      </div>
    </template>
    <div v-if="props.canRemove" class="p-RegularHour__remove c-button c-button--icon">
      <span class="c-button__item c-button__item" @click="removeSetting(index)">
        <img :src="iconCrossPath" alt="" class="c-button__icon">
        {{ $t('pages.CompaniesLocationStore.businessHours.wdayIndexRegularHours.deleteSetting') }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.p-RegularHour {
  &__header {
    vertical-align: baseline;
  }
  &__label {
    padding: 0 0 12px;
    font-weight: bold;
    font-size: 18px;
    display: inline-block;
    width: 150px;
  }
  &__form {
    display: inline-flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 24px -8px 0;
  }
  &__combination {
    margin-top: 20px;
    display: flex;
    &__checkbox {
      margin-right: 12px;
    }
  }
  &-period {
    &__form {
      padding: 0 8px 8px;
    }
    &-remove {
      padding: 32px 0 12px 0;
      &__button {
        cursor: pointer;
        display: inline-flex;
        flex-wrap: wrap;
      }
    }
    &__add {
      margin-top: 0;
    }
  }
  &__remove {
    margin-top: 0;
    &__button {
      cursor: pointer;
      display: inline-flex;
      flex-wrap: wrap;
    }
  }
}

</style>
